import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms';

import { Options } from '@ph-core/enums/options.enum';
import { MaxLengthValidation } from '@ph-core/validations/custom.validation';
import {
  aprLimitValidator,
  characterValidator,
  engineCCLimitValidator,
  engineCCValidator,
  HinInvalidCharValidator,
  HinLimitValidator,
  PidInvalidCharValidator,
  PidLimitValidator,
  TinInvalidCharValidator,
  TinLimitValidator,
  unitIdCharValidator,
  unitIdValidator,
  VinInvalidCharValidator,
  VinLimitValidator,
} from '@ph-core/validations/vin.validation';
import { DecodeVinResponse } from '@ph-model/api/response/decode-vin.response';
import { Country, CountryPostalLengths } from '@ph-model/country-code';
import { Field } from '@ph-model/form/field';

import * as fromValidations from '../validations/generic.validation';
import {
  DateRangeValidator,
  EmailValidator,
  GreaterThanZeroNumberValidator,
  NumberValidator,
  RequiredDateValidator,
  ZipCodeValidator,
} from '../validations/generic.validation';

export interface ExtendedFormControl extends FormControl {
  label?: string;
  taxPercentage?: number;
}

export const getFormattedPostalCode: (postalCode: string) => string = (postalCode: string): string => {
  if (!postalCode) {
    return '';
  }
  let formattedValue = postalCode.replaceAll(' ', '');

  if (formattedValue.length === 6) {
    // Canadian postal code, that have to be separated with space
    formattedValue = `${formattedValue.slice(0, 3)} ${formattedValue.slice(3)}`;
  }

  return formattedValue.toUpperCase();
};

@Injectable({ providedIn: 'root' })
export class FormService {
  private formBuilder = inject(FormBuilder);

  triggerValidityOfAllFields(form: FormGroup): void {
    Object.keys(form.controls).forEach((field: string) => form.get(field)?.markAsTouched({ onlySelf: true }));
  }

  // CONVERT FIELD ARRAY TO FORM GROUP
  setForm(fields: Field[]): FormGroup {
    const form: FormGroup = this.formBuilder.group({});
    fields.forEach((element: Field) => {
      if (element.display) {
        const field = this._setDefault(element);
        const validations = this.getValidations(element);
        field.setValidators(validations);
        form.addControl(element.name, field);
      }
    });
    this._businessFieldValidate(form);

    return form;
  }

  setProductFormAfterVinDecodedSuccessfully(productForm: FormGroup, vinData: DecodeVinResponse): void {
    productForm.get('vin')?.setValue(vinData.vin);
    productForm.get('year')?.setValue(vinData.year[0]);
    productForm.get('make')?.setValue(vinData.make[0]);
    productForm.get('model')?.setValue(vinData.model[0]);
    productForm.get('trim')?.setValue(vinData.trim[0]);
  }

  setProductFormForManualDecode(productForm: FormGroup): void {
    if (productForm) {
      // don't overwrite existing values in case we're loading a stored form
      if (!productForm.get('year')?.value) {
        productForm.get('year')?.setValue(null);
      }
      if (!productForm.get('make')?.value) {
        productForm.get('make')?.setValue(null);
      }
      if (!productForm.get('model')?.value) {
        productForm.get('model')?.setValue(null);
      }
      if (!productForm.get('trim')?.value) {
        productForm.get('trim')?.setValue(null);
      }
    }
  }

  getValidations(element: Field): ValidatorFn[] {
    let validations: ValidatorFn[] = [];
    if (element.required) {
      validations.push(fromValidations.RequiredValidator);
    }
    element.validations.forEach((elementValidation: string) => {
      validations = this._addValidation(validations, elementValidation, element.dateRange);
    });

    return validations;
  }

  getFormattedPostalCode(postalCode: string): string {
    return getFormattedPostalCode(postalCode);
  }

  setCountryCode(postalCode: string): string {
    if (postalCode === null) {
      return '';
    }
    const postalCodeLength: CountryPostalLengths = postalCode.length;

    if (postalCodeLength === CountryPostalLengths.USAPostal) {
      return Country.USA;
    }
    if (
      postalCodeLength === CountryPostalLengths.CANPostalWS ||
      postalCodeLength === CountryPostalLengths.CANPostalWIS
    ) {
      return Country.CAN;
    }

    return '';
  }

  private _setDefault(element: Field): ExtendedFormControl {
    const field: ExtendedFormControl = new FormControl();
    field.label = element.label;
    field.setValue(element.default);
    if (element.readOnly) {
      field.disable();
    }
    // only for tax fields
    if (element.taxPercentage) {
      field.taxPercentage = element.taxPercentage / 100.0;
    }

    return field;
  }

  private _addValidation(
    validateArray: ValidatorFn[] = [],
    validation: string,
    dateRange: [number, number] | undefined
  ): ValidatorFn[] {
    /// Add all Validation checks here
    if (validation === 'postalcode') {
      validateArray.push(ZipCodeValidator);
    }
    /// Add all Validation checks here
    if (validation === 'daterequired') {
      validateArray.push(RequiredDateValidator);
    }
    if (validation === 'daterange' && dateRange) {
      validateArray.push(DateRangeValidator(dateRange));
    }
    if (validation === 'email') {
      validateArray.push(EmailValidator);
    }
    if (validation === 'vinlimit') {
      validateArray.push(VinLimitValidator);
    }
    if (validation === 'vininvalidchar') {
      validateArray.push(VinInvalidCharValidator);
    }
    if (validation === 'pidlimit') {
      validateArray.push(PidLimitValidator);
    }
    if (validation === 'pidnvalidchar') {
      validateArray.push(PidInvalidCharValidator);
    }
    if (validation === 'tinlimit') {
      validateArray.push(TinLimitValidator);
    }
    if (validation === 'tininvalidchar') {
      validateArray.push(TinInvalidCharValidator);
    }
    if (validation === 'hinlimit') {
      validateArray.push(HinLimitValidator);
    }
    if (validation === 'hininvalidchar') {
      validateArray.push(HinInvalidCharValidator);
    }
    if (validation === 'numeric') {
      validateArray.push(engineCCValidator);
    }
    if (validation === 'character') {
      validateArray.push(characterValidator);
    }
    if (validation === 'engineMaxLength') {
      validateArray.push(engineCCLimitValidator);
    }
    if (validation === 'aprLimit') {
      validateArray.push(aprLimitValidator);
    }
    if (validation === 'unitIdLimit') {
      validateArray.push(unitIdValidator);
    }
    if (validation === 'unitIdvalidchar') {
      validateArray.push(unitIdCharValidator);
    }
    if (validation === 'numbersOnly') {
      validateArray.push(NumberValidator);
    }
    if (validation === 'numberGreaterThanZero') {
      validateArray.push(GreaterThanZeroNumberValidator);
    }
    if (validation === 'odometerMaxLength') {
      validateArray.push(MaxLengthValidation(7, 'errors.odometerLength'));
    }

    return validateArray;
  }

  private _businessFieldValidate(form: FormGroup): void {
    if (form.get('businessFlag')) {
      const businessField = form.get('businessFlag')?.value;
      if (businessField === Options.NO) {
        form.get('businessName')?.disable();
        form.get('firstName')?.enable();
        form.get('middleInitial')?.enable();
        form.get('lastName')?.enable();
      }
      if (businessField === Options.YES) {
        form.get('businessName')?.enable();
        form.get('firstName')?.disable();
        form.get('middleInitial')?.disable();
        form.get('lastName')?.disable();
      }
    }
  }
}
